import { InfoTooltip } from '@/Components/ui/Tooltip';
import type { RouterType } from '@/Router';
import type { SetStateFn } from '@/Types';
import { useUserPostCaps } from '@/Utility/auth/Auth';
import { useUser } from '@/Utility/hooks';
import type { Frags_Status_Settings_Fragment$data } from '@/graphql/queries/__generated__/Frags_Status_Settings_Fragment.graphql';
import { PostStatusEnum, type StatusSettings, StatusUiPermissionEnum } from '@/graphql/types';
import { AddTask, ArrowDropDown, Autorenew, Save, Visibility } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  type ButtonProps,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { type ToOptions, createLink } from '@tanstack/react-router';
import { format as dateFormat } from 'date-fns';
import { bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import type { DeepNullable, DeepPartial } from 'ts-essentials';
import styles from '../status/status-dropdown.module.css';
import { PostAccess } from './post-access';
import {
  type NodeTypesForStatus,
  STATUS_GROUPS,
  type StatusGroups,
  type UseStatusProps,
  getIsStatus,
  getStatusGroupsFromSettings,
  getStatusSettingsFromGroups,
  getStatusTypeObject,
  useStatusesAllowed,
} from './utils';

const generateToken = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

  // Generate cryptographically secure random bytes
  const randomBytes = new Uint8Array(32);
  crypto.getRandomValues(randomBytes);

  // Create first part: 16 characters of pure randomness
  let randomPart = '';
  for (let i = 0; i < 16; i++) {
    const index = randomBytes[i] % chars.length;
    randomPart += chars[index];
  }

  let timeComponent = '';
  let timeValue = Date.now();

  // Generate 6 characters representing the time
  for (let i = 0; i < 6; i++) {
    const remainder = timeValue % chars.length;
    timeComponent = chars[remainder] + timeComponent;
    timeValue = Math.floor(timeValue / chars.length);
  }

  return randomPart + timeComponent;
};

const MuiButtonLink = createLink(Button);

// Extend the context to include access control state
const initStatusDropdownContext = {
  radioAdd: STATUS_GROUPS.addAs[PostStatusEnum.Draft].value as StatusGroups['addAs']['value'],
  setRadioAdd: (() => {}) as SetStateFn<StatusGroups['addAs']['value']>,
  radioVisibility: STATUS_GROUPS.visibility[PostStatusEnum.Hidden].value as NonNullable<
    StatusGroups['visibility']
  >['value'],
  setRadioVisibility: (() => {}) as SetStateFn<NonNullable<StatusGroups['visibility']>['value']>,
  accessOption: StatusUiPermissionEnum.Public as StatusUiPermissionEnum,
  setAccessOption: (() => {}) as SetStateFn<StatusUiPermissionEnum>,
};

const StatusDropdownContext = createContext<typeof initStatusDropdownContext>({ ...initStatusDropdownContext });

const useStatusDropdownContext = (): typeof initStatusDropdownContext => {
  return useContext(StatusDropdownContext);
};

type GenericStatusSettings =
  | StatusSettings
  | DeepNullable<DeepPartial<Frags_Status_Settings_Fragment$data>>
  | null
  | undefined;

export type AccessControlProps = {
  enabled?: boolean;
  id?: string | null | undefined;
};

type StatusDropdownComponentProps<T extends keyof NodeTypesForStatus> = {
  /**
   * The input for the status dropdown. Separate from the statusFromDb
   */
  input: GenericStatusSettings;
  /**
   * The original status from the db, this ensures that view can be disabled if the status is draft, pending or trash
   * We can also determine the actual current state of the status before saving
   */
  statusFromDb: GenericStatusSettings;
  usePermissions?: boolean;
  accessControl?: AccessControlProps;

  onChange: (data: StatusSettings) => void;
  disabled?: boolean;
  isFallback?: boolean;
  className?: string;
} & UseStatusProps<T>;

const StatusDropdownComponentDefault = <T extends keyof NodeTypesForStatus>({
  input,
  statusFromDb,
  usePermissions,
  onChange,
  className,
  disabled,
  type,
  statusOptions,
  isFallback,
  accessControl,
}: StatusDropdownComponentProps<T>) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'status-dropdown',
  });
  const allowedStatuses = useStatusesAllowed(type, statusOptions);

  const { radioAdd, setRadioAdd, radioVisibility, setRadioVisibility, accessOption, setAccessOption } =
    useStatusDropdownContext();

  const onChangeHandler = useCallback(
    (data?: StatusSettings | null | undefined) => {
      if (!data) {
        return;
      }

      const groups = getStatusGroupsFromSettings(data);
      if (!groups?.visibility?.value) {
        return;
      }
      setRadioVisibility(groups?.visibility?.value);
      setRadioAdd(groups?.addAs?.value ?? STATUS_GROUPS.addAs[PostStatusEnum.Draft].value);
      setAccessOption(groups?.uiPermission?.value ?? StatusUiPermissionEnum.Public);

      const newData = { ...data };

      newData.uiPermission = {
        type: groups?.uiPermission?.value ?? StatusUiPermissionEnum.Public,
        token: data?.uiPermission?.token ?? input?.uiPermission?.token ?? '',
      };

      if (groups?.uiPermission?.value === StatusUiPermissionEnum.Token) {
        // Handle token case
        let token = data?.uiPermission?.token ?? input?.uiPermission?.token ?? '';
        if (!input?.uiPermission?.token) {
          token = generateToken();

          toast.info('A new token has been generated for this post.');
        }
        newData.uiPermission = {
          type: StatusUiPermissionEnum.Token,
          token: token,
        };
      }

      onChange(newData);
    },
    [onChange, setRadioAdd, setRadioVisibility, setAccessOption]
  );

  const isRadioDisabled = useCallback(
    (
      type: 'add-as' | 'visibility',
      options: Array<NonNullable<StatusGroups['visibility']>['value'] | StatusGroups['addAs']['value']>
    ) => {
      if (type === 'visibility') {
        if (radioAdd === PostStatusEnum.Pending) {
          return false;
        }
        return !options.some((s) => allowedStatuses.find(({ value }) => value === s));
      }
      return false;
    },
    [radioAdd, allowedStatuses]
  );

  useEffect(() => {
    if (statusFromDb) {
      const status = getStatusGroupsFromSettings(statusFromDb as StatusSettings);
      if (status) {
        if (status.addAs.value !== radioAdd) {
          setRadioAdd(status.addAs.value);
        }
        if (status.visibility?.value !== radioVisibility) {
          setRadioVisibility(status.visibility?.value ?? STATUS_GROUPS.visibility[PostStatusEnum.Hidden].value);
        }
      }
    }
  }, [statusFromDb]);

  const user = useUser();
  const userPostCaps = useUserPostCaps(type);

  const canCurrentUserApproveThisPost = useMemo(() => {
    if (!statusOptions?.author?.id || statusOptions?.author?.id === user?.id) {
      return false;
    }

    // current user needs to have the following permissions
    if (
      userPostCaps.publish_posts === false ||
      userPostCaps.publish_hidden_post === false ||
      userPostCaps.edit_others_posts === false
    ) {
      return false;
    }

    // current status needs to be pending
    if (getIsStatus(statusFromDb?.status, [PostStatusEnum.Pending]) === false) {
      return false;
    }

    return true;
  }, [statusOptions, userPostCaps, user?.id, statusFromDb]);

  const isThisPostApproved = useMemo(() => {
    const ret = {
      value: false,
      approvedByCurrentUser: false,
      timestamp: null as string | null,
      date: null as string | null,
    };

    if (getIsStatus(input?.status, [PostStatusEnum.Pending])) {
      return ret;
    }

    if (!!input?.approved?.id) {
      ret.value = true;

      if (
        (statusFromDb?.approved?.id === user?.id && statusFromDb?.approved?.id !== null) ||
        (input?.approved?.id === user?.id && input?.approved?.id !== null)
      ) {
        ret.approvedByCurrentUser = true;
      }

      ret.timestamp = statusFromDb?.approved?.timestamp ?? input?.approved?.timestamp ?? null;

      if (statusFromDb?.approved?.timestamp) {
        // save the date in the format of Fri 1st of jan 2021
        ret.date = dateFormat(new Date(statusFromDb.approved.timestamp ?? input?.approved?.timestamp), 'EEE do MMM yy');
      }
    }

    return ret;
  }, [input, statusFromDb?.approved, user?.id]);

  const buttonContent = useMemo(() => {
    if (isFallback) {
      return <LinearProgress color="inherit" sx={{ width: 100 }} />;
    }

    const settings = getStatusSettingsFromGroups(radioAdd, radioVisibility, accessOption);
    const groups = getStatusGroupsFromSettings(settings!);

    const visibilityIcon = !!groups?.visibility?.icon ? (
      <>
        {' | '} {groups?.visibility?.icon}
      </>
    ) : (
      <></>
    );

    const accessControlIcons =
      accessControl?.enabled && groups?.uiPermission?.icon ? (
        <>
          {' | '} {groups?.uiPermission?.icon}
        </>
      ) : (
        <></>
      );

    const iconography = (
      <>
        {visibilityIcon} {accessControlIcons}
      </>
    );

    if (canCurrentUserApproveThisPost) {
      return (
        <>
          <AddTask />
          <Typography variant="button">Requires Approval</Typography>
          {iconography}
        </>
      );
    }

    const statusObject = getStatusTypeObject(settings);
    return (
      <>
        {groups?.addAs?.icon}
        <Typography variant="button">{isThisPostApproved.value ? 'Approved' : statusObject?.label}</Typography>
        {iconography}
      </>
    );
  }, [canCurrentUserApproveThisPost, isThisPostApproved, isFallback, radioAdd, radioVisibility]);

  return (
    <>
      <Tooltip title={`${STATUS_GROUPS.addAs[radioAdd].label} -> ${STATUS_GROUPS.visibility[radioVisibility].label}`}>
        <span>
          <Button
            className={`${styles.dropdownButton} ${className} ${canCurrentUserApproveThisPost && styles.forApproval} ${isThisPostApproved.value && styles.approved}`}
            {...bindTrigger(popupState)}
            disabled={disabled || allowedStatuses?.length === 0 || isFallback}
          >
            {buttonContent}
            <ArrowDropDown
              sx={{
                transform: `rotate(${popupState.isOpen ? '180deg' : '0deg'})`,
                transition: 'transform 0.3s cubic-bezier(0.65, 0.05, 0.36, 1)',
              }}
            />
          </Button>
        </span>
      </Tooltip>

      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              borderRadius: 1,
              boxShadow: 6,
              minWidth: 250,
            },
          },
        }}
      >
        <Stack spacing={2}>
          {isThisPostApproved.value && (
            <div>
              <Typography variant="body2" color="text.secondary" sx={{ cursor: 'default' }}>
                {isThisPostApproved.approvedByCurrentUser ? 'You' : (statusFromDb?.approved?.user?.name ?? 'Someone')}{' '}
                approved this post{' '}
                {isThisPostApproved.date && (
                  <Tooltip title={dateFormat(new Date(isThisPostApproved?.timestamp ?? ''), 'PPpp')}>
                    <Typography variant="caption" color="text.secondary" fontStyle="italic">
                      {isThisPostApproved.date}
                    </Typography>
                  </Tooltip>
                )}
              </Typography>
            </div>
          )}

          <FormControl
            sx={{
              // hide the add as approved if the current user can approve this post
              // instead we show an approval button
              ...(canCurrentUserApproveThisPost && { display: 'none' }),
            }}
          >
            <FormLabel id="status-dropdown-add-as" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Add As
            </FormLabel>
            <RadioGroup
              name="add-as"
              value={radioAdd}
              onChange={(e) => {
                const value = (e.target as HTMLInputElement).value as StatusGroups['addAs']['value'];
                setRadioAdd(value);

                onChangeHandler({
                  ...getStatusSettingsFromGroups(value, radioVisibility, accessOption)!,
                  approved: null,
                });
              }}
            >
              <StatusRadioOption
                value={STATUS_GROUPS.addAs[PostStatusEnum.Draft].value}
                label={STATUS_GROUPS.addAs[PostStatusEnum.Draft].label}
                tooltip={STATUS_GROUPS.addAs[PostStatusEnum.Draft].tooltip(type)}
                hidden={statusOptions?.exclude?.includes(PostStatusEnum.Draft)}
                slotProps={{
                  typography: {
                    color: 'text.secondary',
                  },
                }}
              />
              <StatusRadioOption
                value={STATUS_GROUPS.addAs[PostStatusEnum.Pending].value}
                label={STATUS_GROUPS.addAs[PostStatusEnum.Pending].label}
                tooltip={STATUS_GROUPS.addAs[PostStatusEnum.Pending].tooltip(type)}
                hidden={statusOptions?.exclude?.includes(PostStatusEnum.Pending)}
                slotProps={{
                  typography: {
                    color: 'text.secondary',
                  },
                }}
              />
              <StatusRadioOption
                value={STATUS_GROUPS.addAs.add_as_approved.value}
                label={STATUS_GROUPS.addAs.add_as_approved.label}
                tooltip={STATUS_GROUPS.addAs.add_as_approved.tooltip(type)}
                disabled={
                  [PostStatusEnum.Publish, PostStatusEnum.Hidden].some((s) =>
                    allowedStatuses.find(({ value }) => value === s)
                  ) === false
                }
                infoTooltipProps={{
                  disabled:
                    [PostStatusEnum.Publish, PostStatusEnum.Hidden].some((s) =>
                      allowedStatuses.find(({ value }) => value === s)
                    ) === false,
                }}
                slotProps={{
                  typography: {
                    color: 'text.secondary',
                  },
                }}
              />
            </RadioGroup>
          </FormControl>

          <FormControl>
            <FormLabel id="status-dropdown-visibility" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Visibility
            </FormLabel>
            <RadioGroup
              name="visibility"
              value={radioVisibility}
              onChange={(e) => {
                const value = (e.target as HTMLInputElement).value as NonNullable<StatusGroups['visibility']>['value'];

                onChangeHandler(getStatusSettingsFromGroups(radioAdd, value, accessOption));
              }}
            >
              <StatusRadioOption
                value={STATUS_GROUPS.visibility[PostStatusEnum.Publish].value}
                label={STATUS_GROUPS.visibility[PostStatusEnum.Publish].label}
                tooltip={STATUS_GROUPS.visibility[PostStatusEnum.Publish].tooltip(type)}
                hidden={statusOptions?.exclude?.includes(PostStatusEnum.Publish)}
                disabled={isRadioDisabled('visibility', [PostStatusEnum.Publish])}
                slotProps={{
                  typography: {
                    color: 'text.secondary',
                  },
                }}
              />
              <StatusRadioOption
                value={STATUS_GROUPS.visibility[PostStatusEnum.HiddenBS].value}
                label={STATUS_GROUPS.visibility[PostStatusEnum.HiddenBS].label}
                tooltip={STATUS_GROUPS.visibility[PostStatusEnum.HiddenBS].tooltip(type)}
                hidden={statusOptions?.exclude?.includes(PostStatusEnum.HiddenBS)}
                disabled={isRadioDisabled('visibility', [PostStatusEnum.HiddenBS])}
                slotProps={{
                  typography: {
                    color: 'text.secondary',
                  },
                }}
              />
              <StatusRadioOption
                value={STATUS_GROUPS.visibility[PostStatusEnum.Hidden].value}
                label={STATUS_GROUPS.visibility[PostStatusEnum.Hidden].label}
                tooltip={STATUS_GROUPS.visibility[PostStatusEnum.Hidden].tooltip(type)}
                hidden={statusOptions?.exclude?.includes(PostStatusEnum.Hidden)}
                disabled={isRadioDisabled('visibility', [PostStatusEnum.Hidden])}
                slotProps={{
                  typography: {
                    color: 'text.secondary',
                  },
                }}
              />
              {/* both private_restricted and hidden are excluded */}
              <StatusRadioOption
                hidden={
                  !(
                    usePermissions &&
                    !(
                      statusOptions?.exclude?.includes(PostStatusEnum.Hidden) ||
                      statusOptions?.exclude?.includes('private_restricted')
                    )
                  )
                }
                value={STATUS_GROUPS.visibility.private_restricted.value}
                label={STATUS_GROUPS.visibility.private_restricted.label}
                tooltip={STATUS_GROUPS.visibility.private_restricted.tooltip(type)}
                disabled={isRadioDisabled('visibility', [PostStatusEnum.Hidden])}
                slotProps={{
                  typography: {
                    color: 'text.secondary',
                  },
                }}
              />
            </RadioGroup>
          </FormControl>

          {accessControl?.enabled && (
            <FormControl>
              <FormLabel id="status-dropdown-access-control" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                Access Control
              </FormLabel>
              <RadioGroup
                name="access-control"
                value={accessOption}
                onChange={(e) => {
                  const value = e.target.value as StatusUiPermissionEnum;
                  setAccessOption(value);

                  onChangeHandler(getStatusSettingsFromGroups(radioAdd, radioVisibility, value));
                }}
              >
                <StatusRadioOption
                  value={StatusUiPermissionEnum.Public}
                  label="URL Only (default)"
                  tooltip="Anyone with the link can access. No restrictions."
                  containerProps={{ gap: 8 }}
                />
                <StatusRadioOption
                  value={StatusUiPermissionEnum.Token}
                  label="URL + Token"
                  tooltip="A unique token is required in the URL for access. It will automatically be applied when sharing through Zngly. This makes sharing more secure and less reliant on guessing the slug"
                  containerProps={{ gap: 8 }}
                />
                <StatusRadioOption
                  value={StatusUiPermissionEnum.Otp}
                  label="Email Verification"
                  tooltip="Only specified email addresses will be able to access this post."
                  containerProps={{ gap: 8 }}
                />
              </RadioGroup>

              {accessOption === StatusUiPermissionEnum.Otp && !!accessControl?.id ? (
                <Box sx={{ mt: 2 }}>
                  <PostAccess type={type} id={accessControl?.id} />
                </Box>
              ) : null}

              {input?.uiPermission?.token && accessOption === StatusUiPermissionEnum.Token && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Token: <strong>{input.uiPermission.token}</strong>
                  </Typography>
                  <Tooltip title="Re-generate Token. Doing this will invalidate the current token and generate a new one.">
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 'auto', padding: 0 }}
                      onClick={() => {
                        const shouldContinue = confirm(
                          'Are you sure you want to generate a new token? This will invalidate the current token.'
                        );
                        if (!shouldContinue) {
                          return;
                        }

                        const newToken = generateToken();
                        const settings = getStatusSettingsFromGroups(radioAdd, radioVisibility, accessOption);

                        onChangeHandler({
                          ...settings,
                          uiPermission: {
                            ...settings?.uiPermission,
                            token: newToken,
                          },
                        });
                        toast.success('A new token has been generated.');
                      }}
                    >
                      <Autorenew />
                    </Button>
                  </Tooltip>
                </Box>
              )}
            </FormControl>
          )}

          {canCurrentUserApproveThisPost && (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => {
                const value = STATUS_GROUPS.addAs.add_as_approved.value;
                setRadioAdd(value);

                onChangeHandler({
                  ...getStatusSettingsFromGroups(value, radioVisibility, accessOption),
                  approved: {
                    id: user?.id,
                  },
                });

                popupState.close();
                toast.success('Changes will be applied on save');
              }}
            >
              Approve
            </Button>
          )}
        </Stack>
      </Popover>
    </>
  );
};

type StatusDropdownProps<T extends keyof NodeTypesForStatus> = {
  /**
   * Slot props for the status dropdown
   */
  slotProps?: {
    /**
     * View link button - on the left
     */
    view?: {
      link: string | ToOptions<RouterType>;
      text?: string;
      disabled?: boolean;
    };

    /**
     * Save button - on the right
     */
    save?: {
      text?: string;
      onClick?: () => void;
      disabled?: boolean;
    };
    buttonLeft?: {
      text?: string;
      button: ButtonProps;
    };
    buttonRight?: {
      text?: string;
      button: ButtonProps;
    };
  };
} & StatusDropdownComponentProps<T>;

const WithView = <T extends keyof NodeTypesForStatus>(
  props: NonNullable<NonNullable<StatusDropdownProps<T>['slotProps']>['view']> & {
    statusFromDb: GenericStatusSettings;
    type: T;
  }
) => {
  const canViewPost = useMemo(() => {
    if (!props?.statusFromDb || props?.disabled) {
      return false;
    }

    const statusFromDb = props.statusFromDb;
    // given the status from the db, we can determine if the user can view the post,
    // we cannot view if the status is draft, pending or trash
    if (getIsStatus(statusFromDb.status, [PostStatusEnum.Draft, PostStatusEnum.Pending, PostStatusEnum.Trash])) {
      return false;
    }

    return true;
  }, [props]);

  if (typeof props?.link === 'object') {
    return (
      <MuiButtonLink
        className={`${styles.button} ${styles.rightButton}`}
        {...props.link}
        target="_blank"
        startIcon={<Visibility />}
        disabled={canViewPost === false}
      >
        <Typography>{props.text ?? 'View'}</Typography>
      </MuiButtonLink>
    );
  }

  return (
    <Button
      component="a"
      className={`${styles.button} ${styles.rightButton}`}
      href={props.link}
      target="_blank"
      startIcon={<Visibility />}
      disabled={canViewPost === false}
    >
      <Typography>{props.text ?? 'View'}</Typography>
    </Button>
  );
};

const WithSave = <T extends keyof NodeTypesForStatus>(
  props: NonNullable<NonNullable<StatusDropdownProps<T>['slotProps']>['save']> & {
    statusFromDb: GenericStatusSettings;
    type: T;
    statusOptions?: UseStatusProps<T>['statusOptions'];
  }
) => {
  const { radioAdd } = useStatusDropdownContext();
  const userPostCaps = useUserPostCaps(props.type);
  const user = useUser();

  // if the status is in an approved state and the current user does not have the permission to publish posts
  // then we disable the save button
  const canSavePost = useMemo(() => {
    if (props?.disabled) {
      return false;
    }

    const postAuthor = props?.statusOptions?.author?.id;

    // if the current user is not the author of the post
    if (postAuthor !== null && postAuthor !== user?.id) {
      if (userPostCaps.edit_others_posts === false) {
        return false;
      }
    }

    // if the current status is approved,
    if (radioAdd === STATUS_GROUPS.addAs.add_as_approved.value) {
      if (userPostCaps.publish_posts === false && userPostCaps.publish_hidden_post === false) {
        return false;
      }
    }

    return true;
  }, [props, userPostCaps, user?.id, radioAdd]);

  return (
    <Button
      className={`${styles.button} ${styles.leftButton}`}
      onClick={props?.onClick}
      disabled={props?.disabled || canSavePost === false}
      startIcon={<Save />}
    >
      <Typography>{props.text ?? 'Save'}</Typography>
    </Button>
  );
};

type StatusRadioOptionProps = {
  value: string;
  label: string;
  tooltip: string;
  disabled?: boolean;
  hidden?: boolean;
  slotProps?: Record<string, unknown>;
  containerProps?: React.HTMLAttributes<HTMLDivElement>['style'];
  infoTooltipProps?: Record<string, unknown>;
  control?: React.ReactNode;
};

const StatusRadioOption = (props: StatusRadioOptionProps) => {
  if (props?.hidden) return null;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <FormControlLabel
        value={props?.value}
        control={<Radio />}
        label={props?.label}
        slotProps={props?.slotProps}
        disabled={props?.disabled}
      />
      <InfoTooltip title={props?.tooltip} {...props?.infoTooltipProps} />
    </div>
  );
};

/**
 * Status dropdown component
 * Based on the slot props, it can render a view button on the left and a save button on the right
 */
const StatusDropdownReducer = <T extends keyof NodeTypesForStatus>({ slotProps, ...props }: StatusDropdownProps<T>) => {
  if (!!slotProps && (!!slotProps?.view || !!slotProps?.save || !!slotProps?.buttonLeft || !!slotProps?.buttonRight)) {
    return (
      <ButtonGroup>
        {slotProps?.save && (
          <WithSave
            type={props.type}
            {...slotProps.save}
            statusFromDb={props?.statusFromDb}
            statusOptions={props?.statusOptions}
          />
        )}
        {!!slotProps?.buttonLeft && !slotProps?.view && (
          <Button
            {...slotProps.buttonLeft.button}
            className={`${styles.button} ${styles.leftButton} ${slotProps.buttonLeft?.button?.className}`}
          >
            <Typography>{slotProps.buttonLeft.text}</Typography>
          </Button>
        )}

        <StatusDropdown {...props} className={!!slotProps?.view || slotProps?.buttonRight ? styles.centerButton : ''} />

        {slotProps?.view && <WithView type={props.type} {...slotProps.view} statusFromDb={props?.statusFromDb} />}
        {slotProps?.buttonRight && !slotProps?.save && (
          <Button
            {...slotProps.buttonRight.button}
            className={`${styles.button} ${styles.rightButton} ${slotProps.buttonRight?.button?.className}`}
          >
            <Typography>{slotProps.buttonRight.text}</Typography>
          </Button>
        )}
      </ButtonGroup>
    );
  }

  return <StatusDropdownComponentDefault {...props} />;
};

export const StatusDropdown = <T extends keyof NodeTypesForStatus>(props: StatusDropdownProps<T>) => {
  const [radioAdd, setRadioAdd] = useState<StatusGroups['addAs']['value']>(
    STATUS_GROUPS.addAs[PostStatusEnum.Draft].value
  );
  const [radioVisibility, setRadioVisibility] = useState<NonNullable<StatusGroups['visibility']>['value']>(
    props?.statusOptions?.exclude?.includes(PostStatusEnum.Hidden)
      ? STATUS_GROUPS.visibility[PostStatusEnum.Publish].value
      : STATUS_GROUPS.visibility[PostStatusEnum.Hidden].value
  );

  const [accessOption, setAccessOption] = useState<StatusUiPermissionEnum>(StatusUiPermissionEnum.Public);

  useEffect(() => {
    if (props.input) {
      const status = getStatusGroupsFromSettings(props.input as StatusSettings);
      if (status) {
        if (status.addAs.value !== radioAdd) {
          setRadioAdd(status.addAs.value);
        }
        if (status.visibility?.value !== radioVisibility) {
          let vis = status.visibility?.value;
          if (props?.statusOptions?.exclude?.includes(PostStatusEnum.Hidden)) {
            vis = STATUS_GROUPS.visibility[PostStatusEnum.Publish].value;
          } else {
            vis = vis ?? STATUS_GROUPS.visibility[PostStatusEnum.Hidden].value;
          }
          setRadioVisibility(vis);
        }
        if (status.uiPermission) {
          setAccessOption(status.uiPermission.value);
        }
      }
    }
  }, [props.input]);

  return (
    <StatusDropdownContext.Provider
      value={{
        radioAdd,
        setRadioAdd,
        radioVisibility,
        setRadioVisibility,
        accessOption,
        setAccessOption,
      }}
    >
      <StatusDropdownReducer {...props} />
    </StatusDropdownContext.Provider>
  );
};
